/* LMC Orlando — Article TOC (Chunk 16).

   <lmc-article-toc> custom element:
   1. On connect, finds the nearest .lmc-article__body
   2. Walks all <h2> elements, assigns slug IDs (or appends suffix on collisions)
   3. Builds an <ol> inside the element with anchor links to each H2
   4. Sets up IntersectionObserver to add .is-active to the current heading's TOC item
   5. Smooth-scrolls on click
   6. Cleans up listeners + observer in disconnectedCallback */

(function () {
  'use strict';

  class LMCArticleToc extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
    }

    connectedCallback() {
      // Scope the body lookup to the surrounding layout so we don't pick
      // a stray .lmc-article__body from somewhere else on the page (e.g.,
      // a preview embed). Falls back to document-level lookup only if no
      // layout wrapper is found.
      const layout = this.closest('.lmc-article__layout');
      const body = (layout || document).querySelector('.lmc-article__body');
      if (!body) {
        this.hidden = true;
        return;
      }
      const headings = Array.from(body.querySelectorAll('h2'));
      if (headings.length === 0) {
        this.hidden = true;
        return;
      }

      // Pre-reserve any merchant-authored heading IDs so generated slugs
      // can't collide with them.
      const usedSlugs = new Set();
      headings.forEach((h) => {
        if (h.id) usedSlugs.add(h.id);
      });

      this.tocItems = headings.map((h, i) => {
        if (h.id) {
          return { heading: h, slug: h.id, text: h.textContent.trim() };
        }
        let slug = slugify(h.textContent || ('section-' + (i + 1)));
        let candidate = slug;
        let n = 2;
        while (usedSlugs.has(candidate)) {
          candidate = slug + '-' + n;
          n += 1;
        }
        usedSlugs.add(candidate);
        h.id = candidate;
        return { heading: h, slug: h.id, text: h.textContent.trim() };
      });

      this.renderList();
      this.list.addEventListener('click', this.onClick);

      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '-30% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      this.tocItems.forEach((item) => this.observer.observe(item.heading));
    }

    disconnectedCallback() {
      this.list?.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
    }

    renderList() {
      const headingId = this.getAttribute('data-heading-id') || 'lmc-article-toc-heading';
      const headingText = this.getAttribute('data-heading') || 'In this article';
      const ol = document.createElement('ol');
      this.tocItems.forEach((item) => {
        const li = document.createElement('li');
        li.dataset.tocFor = item.slug;
        const a = document.createElement('a');
        a.href = '#' + item.slug;
        a.textContent = item.text;
        li.appendChild(a);
        ol.appendChild(li);
      });

      // Replace existing children with the heading + list (preserve any
      // server-rendered meta below).
      const meta = this.querySelector('.lmc-article__toc-meta');
      this.innerHTML = '';
      const h4 = document.createElement('h4');
      h4.id = headingId;
      h4.textContent = headingText;
      this.appendChild(h4);
      this.appendChild(ol);
      if (meta) this.appendChild(meta);
      this.list = ol;
    }

    onClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      const targetId = link.getAttribute('href').slice(1);
      const target = document.getElementById(targetId);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.setActive(targetId);
    }

    onIntersect(entries) {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length === 0) return;
      this.setActive(visible[0].target.id);
    }

    setActive(slug) {
      if (!this.list) return;
      Array.from(this.list.children).forEach((li) => {
        li.classList.toggle('is-active', li.dataset.tocFor === slug);
      });
    }
  }

  function slugify(text) {
    return String(text)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) || 'section';
  }

  if (!customElements.get('lmc-article-toc')) {
    customElements.define('lmc-article-toc', LMCArticleToc);
  }
})();
